import { useBehavior, useButton, useTriggerAction, useUpdateWidget, useUpdateWidgetValue } from '@libs/hooks';
import { TButtonData, TCalculatorTopUpData, TCommonDataObj, TWidget, TWidgetProps } from '@libs/types';
import { debounce } from '@libs/utils';
import React, { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { TConsumerItem } from '../../types/data';
import { EConsumerLocalStorageItems } from '../../types/enums';
import { useDetectKeyboard } from '../../hooks/useDetectKeyboard';
import { getInlineStyles } from '../../utils/styles';
import styles from './Calculator.module.css';
import { CalculatorButton } from './CalculatorButton';
import { Fee } from './fee/Fee';
import { CalculatorInput } from './input/CalculatorInput';
import { SubComponent } from './input/SubComponent';
import { useFontSize } from './utils/fontSizeService';
import { toFixedSum } from './utils/format/format';
import { ActionNameEnum, CurrencyDropdownEnum, InputEnum, TResultServerCalculationFunc } from './utils/types';
import { getCurrency, getErrors, getTopUpError, getWaitFor, isCanDoRequest } from './utils/widgets';


const CalculatorTopUp = ({ widget }: TWidgetProps<TCalculatorTopUpData>) => {
    const { keyboardHeight, isKeyboardOpen } = useDetectKeyboard();
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { updateWidgetValueByKey } = useUpdateWidgetValue(widget);
    const [isLoading, setLoading] = useState(false);
    const [isChangedValue, setChangedValue] = useState(false);
    const uid = widget.uid;
    const { mutableWidget: initialWidget } = useUpdateWidget(uid);
    const { triggerAction, triggerActionsByType, triggerActionsByNameAndType } = useTriggerAction(initialWidget as TWidget<TCalculatorTopUpData>);
    const { isDisabled } = useBehavior(initialWidget as TWidget<TCalculatorTopUpData>);
    const { handleClickByActionName } = useButton(initialWidget as TWidget<TButtonData>, { isDisabled, triggerActionsByType, triggerActionsByNameAndType });
    const value = initialWidget?.data?.value as TCommonDataObj;
    const inputValue = (value?.inputValue as string) ?? '0';
    const inputCurrency = (value?.inputCurrency as string) ?? '';
    const { fontSize, updateFontSize } = useFontSize(inputValue);
    const [currentWidget, setCurrentWidget] = useState(initialWidget as TWidget<TCalculatorTopUpData>);
    const messages = currentWidget?.data?.messages ?? {};
    const fee = currentWidget?.data?.fee ?? {};
    const currentError = useMemo(() => getTopUpError(currentWidget?.data?.errors ?? []), [currentWidget?.data?.errors]);
    const currency = useMemo(
        () => getCurrency(inputCurrency, currentWidget?.data.listCurrency),
        [inputCurrency, currentWidget?.data.listCurrency],
    );
    const waitFor = useMemo(() => getWaitFor(currentWidget?.actions), [currentWidget?.actions]);
    const hasErrorInputValue = currentError && Object.keys(currentError).length > 0;
    const isEnabled = Boolean(
        +inputValue &&
        !isChangedValue &&
        !inputValue.endsWith('.') &&
        Boolean(currentWidget.data.buttonContinueEnabled) &&
        !isLoading &&
        (hasErrorInputValue ? currentError.withdrawButtonVisible : true),
    );

    const restoreSavedBalance = useEffectEvent(async () => {
        const savedIso = localStorage.getItem(EConsumerLocalStorageItems.PREFERRED_BALANCE);
        if (!savedIso) return;
        if (savedIso.toLowerCase() === inputCurrency?.toLowerCase()) return;

        const isExist = (currentWidget?.data.listCurrency as TConsumerItem[])?.some((currency) => currency.iso?.toLowerCase() === savedIso.toLowerCase());

        if (isExist) {
            await onServerCalculation(savedIso.toUpperCase(), CurrencyDropdownEnum.INPUT_CURRENCY);
        }
    });

    useEffect(() => {
        restoreSavedBalance().then();
    }, []);

    const onServerCalculation = useCallback(
        async (value: string, name: string): Promise<TResultServerCalculationFunc | undefined> => {
            const action = widget.actions?.find((a) => a?.name === ActionNameEnum.onCalculate);
            try {
                if (isCanDoRequest(value, action) && action) {
                    setLoading(true);
                    if (inputValue) {
                        updateFontSize(inputValue);
                    }
                    if (name === CurrencyDropdownEnum.INPUT_CURRENCY) {
                        updateWidgetValueByKey(value ?? '', name);
                    }
                    const result = await triggerAction(action, { isUpdateWidgetByUid: false });
                    setLoading(false);
                    if (result && Object.keys(result).length > 0) {
                        if (name === CurrencyDropdownEnum.INPUT_CURRENCY) {
                            const resultValue = (result?.data?.value as TCommonDataObj) ?? {};
                            const currencyFromResponse = (resultValue?.inputCurrency as string) ?? '';
                            updateWidgetValueByKey(currencyFromResponse, name);
                        }
                        setCurrentWidget(result);
                        const errors = getErrors(result?.data?.errors ?? []);
                        return { value: result?.data?.value, errors };
                    }
                    return { value: {}, errors: {} };
                }
            } catch (e) {
                if (e instanceof Error && e.name !== 'AbortError') {
                    console.error(e);
                }
                return { value: {}, errors: {} };
            } finally {
                setLoading(false);
                setChangedValue(false);
            }
        },
        [widget?.actions, inputValue, triggerAction, updateFontSize, updateWidgetValueByKey],
    );

    const debounceCalcCommission = debounce(timeoutRef, onServerCalculation, waitFor);

    const handleUpdateValue = useCallback(
        (clearValue: string, name: string) => {
            setChangedValue(true);
            updateWidgetValueByKey(clearValue, name);
            if (hasErrorInputValue) {
                setCurrentWidget((prevState) => {
                    return {
                        ...prevState,
                        data: {
                            ...prevState.data,
                            errors: null,
                        },
                    };
                });
            }
            updateFontSize(clearValue);
            debounceCalcCommission(clearValue, name);
        },
        [hasErrorInputValue, debounceCalcCommission, updateFontSize, updateWidgetValueByKey],
    );

    const handleButtonClick = useCallback(() => {
        handleClickByActionName(ActionNameEnum.submit);
    }, [handleClickByActionName]);

    return (
        <div
            className={styles.calculatorFrame}
            data-test={widget.uid}
            style={getInlineStyles({
                variables: {
                    keyboardHeight: isKeyboardOpen ? `${keyboardHeight}px` : '0px',
                },
            })}
        >
            <div className={styles.calculatorFrameTop}>
                <div className={styles.calculatorAmountContainer}>
                    <CalculatorInput
                        name={InputEnum.INPUT_VALUE}
                        value={inputValue}
                        label={messages.inputLabel}
                        fontSize={fontSize}
                        isError={hasErrorInputValue}
                        handleUpdateValue={handleUpdateValue}
                        subComponent={
                            <SubComponent
                                widget={widget}
                                inputName={InputEnum.INPUT_VALUE}
                                dropdownName={CurrencyDropdownEnum.INPUT_CURRENCY}
                                value={inputCurrency}
                                index={currency.currencyIndex}
                                isDisabled={currency.isDisabled}
                                items={currentWidget?.data.listCurrency ?? []}
                                error={currentError}
                                newInputValue={currentError?.maxWithdrawValue}
                                onServerCalculation={onServerCalculation}
                                handleUpdateValue={handleUpdateValue}
                                handleButtonClick={handleButtonClick}
                                searchPlaceholder={messages.searchPlaceholder}
                                noFoundText={messages.noResultFoundText}
                                dropDownHeader={messages.currencyListText}
                                currencyText={messages.currencyListItemSubtitleText}
                                buttonChangeAmountLabel={messages.buttonChangeAmountLabel}
                                isTopUp
                            />
                        }
                    />
                </div>
            </div>
            <div className={styles.calculatorFrameBottom}>
                <Fee
                    bottomSheetButton={messages.feeBottomSheetButton}
                    feeBottomSheetFirstText={messages.feeBottomSheetFirstText}
                    feeBottomSheetSecondText={messages.feeBottomSheetSecondText}
                    feeOptions={messages.feeBottomSheetOptions}
                    sum={toFixedSum(fee.sum)}
                    currency={fee.currency}
                    iconName={fee.iconName}
                    label={messages.feeLabel}
                    text={messages.feeText ?? ''}
                />
            </div>
            <CalculatorButton handleClick={handleButtonClick} label={messages.buttonLabelMain} isEnabled={isEnabled} />
        </div>
    );
};
export { CalculatorTopUp };
